import React, { useCallback, useEffect, useState } from "react";

import * as api from "../api";
import AuditList from "../components/AuditList";
import Button from "../components/Button";
import CreditModal from "../components/modals/Credit";
import KillSwitchModal from "../components/modals/KillSwitch";
import Pill from "../components/Pill";
import Switch from "../components/Switch";
import { secondsToMinutes } from "../logic";
import type { PageProps } from "./types";

const TIERS = ["free", "pro", "team", "enterprise"];
// Personal workspaces only ever hold solo plans — team tiers would all-seat
// a one-human org (the backend rejects them there).
const PERSONAL_TIERS = ["free", "pro"];

const searchWrap: React.CSSProperties = { position: "relative", marginBottom: 14 };
const searchInput: React.CSSProperties = {
  width: "100%",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink-900)",
  background: "#fff",
  border: "1px solid var(--border-strong)",
  borderRadius: 8,
  padding: "11px 13px 11px 38px",
  outline: "none",
};
const th: React.CSSProperties = {
  textAlign: "left",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
  fontWeight: 400,
  padding: "8px 10px",
  borderBottom: "1px solid var(--border-hairline)",
};
const td: React.CSSProperties = { padding: "11px 10px", borderBottom: "1px solid var(--border-hairline)" };
const fieldLabel: React.CSSProperties = { display: "block", fontFamily: "var(--font-sans)", fontWeight: 600, fontSize: 12, color: "var(--ink-700)", marginBottom: 6 };
const fieldInput: React.CSSProperties = { width: "100%", fontFamily: "var(--font-sans)", fontSize: 13.5, color: "var(--ink-900)", background: "#fff", border: "1px solid var(--border-strong)", borderRadius: 8, padding: "9px 12px", outline: "none" };
const h3: React.CSSProperties = { margin: "24px 0 10px", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)" };

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-300)", pointerEvents: "none" }}>
      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function TrialChip({ org }: { org: api.OrgRow }) {
  if (org.trial_status === "none") return <span style={{ color: "var(--text-faint)", fontFamily: "var(--font-mono)", fontSize: 12 }}>—</span>;
  const date = org.trial_ends_at ? new Date(org.trial_ends_at).toLocaleDateString() : "";
  return org.trial_status === "active"
    ? <Pill tone="sky">trial → {date}</Pill>
    : <Pill tone="warn">lapsed {date}</Pill>;
}

export default function Orgs({ effectiveRole, showToast, navigate }: PageProps) {
  const isSuper = effectiveRole === "SUPERADMIN";
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<api.OrgRow[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.searchOrgs(q);
      setRows(res.orgs);
      setTotal(res.total ?? res.orgs.length);
    } catch (e: any) {
      setError(e?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  }, [q]);

  // Debounce search as the operator types.
  useEffect(() => {
    const t = setTimeout(runSearch, 200);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr", height: "100%" }}>
      <section style={{ padding: "24px 28px", overflow: "auto", borderRight: "1px solid var(--border-hairline)" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
          <div style={{ ...searchWrap, flex: 1, marginBottom: 0 }}>
            <SearchIcon />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search orgs by name or id…" style={searchInput} />
          </div>
          {isSuper && (
            <Button variant="secondary" size="sm" onClick={() => { setCreating(true); setSelected(null); }}>New org</Button>
          )}
        </div>
        <p style={{ margin: "0 0 8px", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-faint)" }}>
          {loading ? "loading…" : `${total} result${total === 1 ? "" : "s"}`}
        </p>
        {error && <p style={{ color: "var(--danger-500)", fontSize: 13 }}>{error}</p>}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={th}>Org</th><th style={th}>Tier</th><th style={th}>Dialing</th>
            <th style={{ ...th, textAlign: "right" }}>Seats</th>
          </tr></thead>
          <tbody>
            {rows.map((o) => {
              const sel = o.id === selected;
              const dialingOn = o.dialing_enabled !== false;
              return (
                <tr key={o.id} onClick={() => { setSelected(o.id); setCreating(false); }} style={{ cursor: "pointer", background: sel ? "var(--surface-sunken)" : "transparent", boxShadow: sel ? "inset 3px 0 0 var(--violet-500)" : "none" }}>
                  <td style={td}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-900)" }}>
                      {o.name || o.id}
                      {o.is_personal && <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)", background: "var(--ink-100)", borderRadius: 4, padding: "1px 6px", marginLeft: 8 }}>personal</span>}
                    </div>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{o.id}</div>
                  </td>
                  <td style={td}><Pill tone="neutral">{o.plan_tier || "—"}</Pill></td>
                  <td style={td}><Pill tone={dialingOn ? "ok" : "danger"}>{dialingOn ? "enabled" : "disabled"}</Pill></td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--ink-700)" }}>{o.member_count}{o.seat_limit != null ? ` / ${o.seat_limit}` : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && rows.length === 0 && <p style={{ padding: "24px 10px", color: "var(--text-muted)", fontSize: 13.5 }}>No orgs match “{q}”.</p>}
      </section>

      <section style={{ overflow: "auto", background: "#fff" }}>
        {creating ? (
          <CreateOrgForm onDone={(id) => { setCreating(false); setSelected(id); runSearch(); }} onCancel={() => setCreating(false)} />
        ) : selected ? (
          <OrgDetail key={selected} orgId={selected} isSuper={isSuper} showToast={showToast} navigate={navigate} onChanged={runSearch} />
        ) : (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-faint)", fontSize: 14 }}>Select an org, or create one.</div>
        )}
      </section>
    </div>
  );
}

function Tile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "var(--surface-sunken)", border: "1px solid var(--border-hairline)", borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)" }}>{label}</div>
      <div style={{ marginTop: 8 }}>{children}</div>
    </div>
  );
}

function OrgDetail({
  orgId,
  isSuper,
  showToast,
  navigate,
  onChanged,
}: {
  orgId: string;
  isSuper: boolean;
  showToast: (m: string) => void;
  navigate: PageProps["navigate"];
  onChanged: () => void;
}) {
  const [org, setOrg] = useState<api.OrgDetail | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [audit, setAudit] = useState<api.AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [tier, setTier] = useState("team");
  const [seats, setSeats] = useState(10);
  // Spec 882: class limits held as strings so blank ("") can mean null (no split).
  const [managerSeats, setManagerSeats] = useState("");
  const [memberSeats, setMemberSeats] = useState("");
  const [trialEnd, setTrialEnd] = useState("");
  const [modal, setModal] = useState<null | "kill" | "credit">(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const o = await api.getOrg(orgId);
      setOrg(o);
      setName(o.name || "");
      setTier(o.plan_tier || "free");
      setSeats(o.seat_limit ?? 1);
      setManagerSeats(o.manager_seat_limit != null ? String(o.manager_seat_limit) : "");
      setMemberSeats(o.member_seat_limit != null ? String(o.member_seat_limit) : "");
      setTrialEnd(o.trial_ends_at ? o.trial_ends_at.slice(0, 10) : "");
    } catch (e: any) {
      setError(e?.message || "Failed to load org");
    }
    try {
      const b = await api.getOrgCreditBalance(orgId);
      setBalance(b.balance_seconds);
    } catch {
      setBalance(null);
    }
    try {
      const a = await api.listAudit({ targetOrgId: orgId });
      setAudit(a.entries.slice(0, 6));
    } catch {
      setAudit([]);
    }
  }, [orgId]);

  useEffect(() => { setEditing(false); load(); }, [load]);

  if (!org) return <div style={{ padding: 28 }}>{error ? <p style={{ color: "var(--danger-500)" }}>{error}</p> : <p style={{ color: "var(--text-muted)" }}>Loading…</p>}</div>;

  const dialingOn = org.dialing_enabled !== false;
  const canManage = isSuper && !org.is_personal; // kill-switch + full edit form — not personal orgs
  // Personal workspaces: plan_tier-only comp control (free/pro) — the backend
  // rejects everything else on personal orgs.
  const canCompPlan = isSuper && org.is_personal;
  // Credits DO apply to personal workspaces — a solo user's balance lives on their
  // personal org, and the backend /adjustments has no personal-org restriction.
  const canAdjustCredits = isSuper;
  const balanceLabel = balance == null ? "unavailable" : `${secondsToMinutes(balance)} min`;

  // Spec 882: blank class input = null for that class (no limit set).
  const seatClass = (v: string): number | null => (v.trim() === "" ? null : Number(v));
  const changeSeatClasses = (nextManager: string, nextMember: string) => {
    setManagerSeats(nextManager);
    setMemberSeats(nextMember);
    const m = seatClass(nextManager);
    const n = seatClass(nextMember);
    // With both classes set, total seats tracks their sum (still overridable below).
    if (m != null && n != null && Number.isFinite(m + n)) setSeats(m + n);
  };

  const saveEdit = async () => {
    setError(null);
    const trimmedName = name.trim();
    // Spec 882: omit class limits when untouched; both blank = explicit nulls (clears the split).
    const mgrSeats = seatClass(managerSeats);
    const memSeats = seatClass(memberSeats);
    const classesTouched = mgrSeats !== org.manager_seat_limit || memSeats !== org.member_seat_limit;
    try {
      await api.updateOrg(orgId, {
        plan_tier: tier,
        seat_limit: seats,
        trial_ends_at: trialEnd ? new Date(`${trialEnd}T23:59:59Z`).toISOString() : null,
        // Spec 753: send name only when actually changed (blank never blanks).
        ...(trimmedName && trimmedName !== (org.name || "") ? { name: trimmedName } : {}),
        ...(classesTouched ? { manager_seat_limit: mgrSeats, member_seat_limit: memSeats } : {}),
      });
      setEditing(false);
      await load();
      onChanged();
      showToast(`Updated ${org.name || orgId} — ${tier}, ${seats} seats.`);
    } catch (e: any) {
      setError(e?.message || "Update failed");
    }
  };

  // Bad-data guard: a personal org should never hold a team tier — clamp so
  // the select's display and the saved payload always agree.
  const personalTier = PERSONAL_TIERS.includes(tier) ? tier : "free";

  const savePlan = async () => {
    setError(null);
    try {
      await api.updateOrg(orgId, { plan_tier: personalTier });
      await load();
      onChanged();
      showToast(`Updated ${org.name || orgId} — ${personalTier}.`);
    } catch (e: any) {
      setError(e?.message || "Update failed");
    }
  };

  return (
    <div style={{ padding: "26px 28px 40px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 22, letterSpacing: "-0.02em", color: "var(--ink-900)" }}>{org.name || org.id}</h2>
          <p style={{ margin: "5px 0 0", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>{org.id} · created {org.created_at ? new Date(org.created_at).toLocaleDateString() : "—"}</p>
        </div>
        <Pill tone={dialingOn ? "ok" : "danger"}>{dialingOn ? "dialing enabled" : "dialing disabled"}</Pill>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 20 }}>
        <Tile label="Tier"><Pill tone="neutral">{org.plan_tier || "—"}</Pill></Tile>
        <Tile label="Seats"><span style={{ fontSize: 15, color: "var(--ink-900)", fontFamily: "var(--font-mono)" }}>{org.member_count}{org.seat_limit != null ? ` / ${org.seat_limit}` : ""}</span></Tile>
        <Tile label="Trial"><TrialChip org={org} /></Tile>
        <Tile label="Call credits"><span style={{ fontSize: 15, color: "var(--ink-900)", fontFamily: "var(--font-mono)" }}>{balanceLabel}</span></Tile>
      </div>

      {error && <p style={{ color: "var(--danger-500)", fontSize: 13, marginTop: 12 }}>{error}</p>}

      {canCompPlan && (
        <div style={{ marginTop: 16, border: "1px solid var(--border-hairline)", borderRadius: 14, padding: 18, background: "var(--surface-sunken)" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--violet-500)", marginBottom: 14 }}>Personal workspace plan</div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}><label style={fieldLabel}>Plan</label>
              <select value={personalTier} onChange={(e) => setTier(e.target.value)} style={{ ...fieldInput, cursor: "pointer" }}>
                {PERSONAL_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <Button variant="primary" size="sm" onClick={savePlan}>Save plan</Button>
          </div>
          <p style={{ margin: "10px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>Comp or revoke solo Pro. The change is audited; name, seats and trials stay locked on personal workspaces.</p>
        </div>
      )}

      {canManage && (
        editing ? (
          <div style={{ marginTop: 16, border: "1px solid var(--border-hairline)", borderRadius: 14, padding: 18, background: "var(--surface-sunken)" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--violet-500)", marginBottom: 14 }}>Edit name, plan &amp; seats</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div style={{ gridColumn: "1 / -1" }}><label style={fieldLabel}>Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder={org.name || "Workspace name"} style={fieldInput} />
              </div>
              <div><label style={fieldLabel}>Plan tier</label>
                <select value={tier} onChange={(e) => setTier(e.target.value)} style={{ ...fieldInput, cursor: "pointer" }}>
                  {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><label style={fieldLabel}>Seat limit</label>
                <input type="number" min={1} max={1000} value={seats} onChange={(e) => setSeats(Number(e.target.value))} style={{ ...fieldInput, fontFamily: "var(--font-mono)" }} />
              </div>
              <div><label style={fieldLabel}>Manager seats ($39) <span style={{ fontWeight: 400, color: "var(--text-faint)" }}>(both blank = no split)</span></label>
                <input type="number" min={1} max={1000} value={managerSeats} onChange={(e) => changeSeatClasses(e.target.value, memberSeats)} style={{ ...fieldInput, fontFamily: "var(--font-mono)" }} />
              </div>
              <div><label style={fieldLabel}>Member seats ($19)</label>
                <input type="number" min={0} max={1000} value={memberSeats} onChange={(e) => changeSeatClasses(managerSeats, e.target.value)} style={{ ...fieldInput, fontFamily: "var(--font-mono)" }} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}><label style={fieldLabel}>Trial ends <span style={{ fontWeight: 400, color: "var(--text-faint)" }}>(blank = no trial)</span></label>
                <input type="date" value={trialEnd} onChange={(e) => setTrialEnd(e.target.value)} style={{ ...fieldInput, fontFamily: "var(--font-mono)" }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <Button variant="primary" size="sm" onClick={saveEdit}>Save changes</Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 14 }}>
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>Edit plan, seats &amp; trial</Button>
          </div>
        )
      )}

      <h3 style={h3}>Owners</h3>
      {org.owners.length
        ? org.owners.map((ow) => <div key={ow.user_id} style={{ fontSize: 13.5, color: "var(--ink-900)", padding: "2px 0" }}>{ow.name || ow.user_id} <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: 12 }}>{ow.email}</span></div>)
        : <span style={{ color: "var(--text-muted)", fontSize: 13 }}>none</span>}

      {/* Dialing kill-switch (US2) */}
      <div style={{ marginTop: 24, border: "1px solid var(--border-hairline)", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "16px 18px", background: "var(--surface-sunken)", borderBottom: "1px solid var(--border-hairline)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--violet-500)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none" }}><path d="M12 2v10" /><path d="M18.4 6.6a9 9 0 1 1-12.77.04" /></svg>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-900)" }}>Dialing kill-switch</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--violet-500)", marginTop: 2 }}>US2 · containment</div>
            </div>
          </div>
          {canManage
            ? <Switch checked={dialingOn} onChange={() => setModal("kill")} ariaLabel="Toggle dialing" />
            : <Pill tone={dialingOn ? "ok" : "danger"}>{dialingOn ? "dialing enabled" : "dialing disabled"}</Pill>}
        </div>
        <div style={{ padding: "14px 18px", fontSize: 12.5, color: "var(--text-muted)", lineHeight: 1.55 }}>
          {org.is_personal
            ? "Kill-switch is not available on personal orgs."
            : canManage
              ? `Flip to ${dialingOn ? "disable" : "enable"} dialing. A reason is required and the change is audited.`
              : "Visible to staff; only a superadmin can change the dialing state."}
        </div>
      </div>

      {/* Prepaid call credits (US3) */}
      <div style={{ marginTop: 16, border: "1px solid var(--border-hairline)", borderRadius: 14, padding: "16px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-900)" }}>Prepaid call credits</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>Balance {balanceLabel} · adjustments are audited (US3)</div>
        </div>
        {canAdjustCredits && balance != null && <Button variant="secondary" size="sm" onClick={() => setModal("credit")}>Adjust credits…</Button>}
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <Button variant="ghost" size="sm" onClick={() => navigate("support", { readinessOrgId: orgId })}>Run readiness check</Button>
        {isSuper && <Button variant="ghost" size="sm" onClick={() => navigate("conversations", { conversationsOrgId: orgId })}>Recent calls</Button>}
      </div>

      <h3 style={{ ...h3, margin: "26px 0 10px" }}>Recent audit</h3>
      <AuditList entries={audit} compact />

      {modal === "kill" && (
        <KillSwitchModal
          orgName={org.name || orgId}
          toDisable={dialingOn}
          onCancel={() => setModal(null)}
          onConfirm={async (reason) => {
            await api.setOrgDialing(orgId, { disabled: dialingOn, reason });
            setModal(null);
            await load();
            onChanged();
            showToast(`Dialing ${dialingOn ? "disabled" : "enabled"} for ${org.name || orgId}.`);
          }}
        />
      )}
      {modal === "credit" && balance != null && (
        <CreditModal
          orgName={org.name || orgId}
          balanceSeconds={balance}
          onCancel={() => setModal(null)}
          onConfirm={async (direction, minutes, reason) => {
            const res = await api.adjustOrgCredits(orgId, { direction, seconds: minutes * 60, reason });
            setModal(null);
            await load();
            onChanged();
            showToast(`${direction === "add" ? "Added" : "Subtracted"} ${minutes} min · balance now ${secondsToMinutes(res.balance_seconds)} min.`);
          }}
        />
      )}
    </div>
  );
}

function CreateOrgForm({ onDone, onCancel }: { onDone: (id: string) => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [tier, setTier] = useState("team");
  const [seats, setSeats] = useState(10);
  const [trialDays, setTrialDays] = useState(30);
  const [ownerQ, setOwnerQ] = useState("");
  const [ownerRows, setOwnerRows] = useState<api.UserRow[]>([]);
  const [owner, setOwner] = useState<api.UserRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const searchOwners = async () => {
    try {
      const res = await api.searchUsers(ownerQ);
      setOwnerRows(res.users.slice(0, 8));
    } catch (e: any) {
      setError(e?.message || "User search failed");
    }
  };

  const submit = async () => {
    if (!owner) { setError("Pick an owner (an existing signed-up user)."); return; }
    setBusy(true);
    setError(null);
    try {
      const org = await api.createOrg({ name, plan_tier: tier, seat_limit: seats, trial_days: trialDays, owner_user_id: owner.id });
      onDone(org.id);
    } catch (e: any) {
      setError(e?.message || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: "26px 28px 40px" }}>
      <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 22, letterSpacing: "-0.02em", color: "var(--ink-900)" }}>New org</h2>
      <p style={{ margin: "5px 0 20px", fontSize: 13, color: "var(--text-muted)" }}>Provision a company org and grant its trial.</p>
      <div style={{ display: "grid", gap: 14, maxWidth: 460 }}>
        <div><label style={fieldLabel}>Name</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Pilot" style={fieldInput} /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div><label style={fieldLabel}>Plan tier</label>
            <select value={tier} onChange={(e) => setTier(e.target.value)} style={{ ...fieldInput, cursor: "pointer" }}>{TIERS.map((t) => <option key={t} value={t}>{t}</option>)}</select>
          </div>
          <div><label style={fieldLabel}>Seat limit</label><input type="number" min={1} max={1000} value={seats} onChange={(e) => setSeats(Number(e.target.value))} style={{ ...fieldInput, fontFamily: "var(--font-mono)" }} /></div>
        </div>
        <div><label style={fieldLabel}>Trial days <span style={{ fontWeight: 400, color: "var(--text-faint)" }}>(0 = no trial)</span></label><input type="number" min={0} max={365} value={trialDays} onChange={(e) => setTrialDays(Number(e.target.value))} style={{ ...fieldInput, fontFamily: "var(--font-mono)" }} /></div>
        <div>
          <label style={fieldLabel}>Owner</label>
          {owner ? (
            <p style={{ fontSize: 13.5 }}>{owner.name || owner.id} <span style={{ color: "var(--text-muted)" }}>{owner.email}</span>{" "}<button onClick={() => setOwner(null)} style={{ background: "none", border: "none", color: "var(--violet-500)", cursor: "pointer", font: "inherit", padding: 0 }}>change</button></p>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8 }}>
                <input value={ownerQ} placeholder="Search users…" onChange={(e) => setOwnerQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchOwners(); } }} style={fieldInput} />
                <Button variant="ghost" size="sm" onClick={searchOwners}>Find</Button>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0" }}>
                {ownerRows.map((u) => (
                  <li key={u.id} style={{ padding: "4px 0" }}>
                    <button onClick={() => setOwner(u)} style={{ background: "none", border: "none", color: "var(--violet-500)", cursor: "pointer", font: "inherit", padding: 0 }}>{u.name || u.id} <span style={{ color: "var(--text-muted)" }}>{u.email}</span></button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        {error && <p style={{ color: "var(--danger-500)", fontSize: 13 }}>{error}</p>}
        <div style={{ display: "flex", gap: 10 }}>
          <Button variant="primary" size="sm" onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create org"}</Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}
